<template>
  <div v-bind:class="[large ? 'vue-wrapper' : 'skeleton-container', dashboarditem.loading || dashboarditem.updating ? 'active' : '']">
    <TheLoader v-if="large" :small="false" :active="dashboarditem.loading || dashboarditem.updating" :update="dashboarditem.updating"/>

    <table v-else class="clickable oddeven sortable skeleton-content">
      <thead>
        <tr>
          <th v-for="(thClass, thIndex) in skeletonThLines" v-bind:key="'sk-' + thIndex" v-bind:class="[thClass]"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(trLine, trIndex) in skeletonTdLines" v-bind:key="'sk-tr-' + trIndex">
          <td v-for="(tdClass, tdIndex) in trLine" v-bind:key="'sk-td-' + trIndex + '-' + tdIndex " v-bind:class="[tdClass]"></td>
        </tr>
      </tbody>
    </table>

    <section class="table-card-container">
      <table class="clickable visible oddeven sortable" :class="{'fixed': large}">
        <thead class="border">
          <tr>
            <th v-for="(heading, index) in dashboarditem.headings" v-bind:key="index" v-bind:class="[heading.class, heading.sortKey === dashboarditem.sort ? sortClassUnsigned : '']" @click="changeSorting(heading.sortKey)">
              {{heading.name}}
              <span id="sorttable_sortrevind" v-if="heading.sortKey === dashboarditem.sort && dashboarditem.direction === 'desc'">&nbsp;</span>
              <span id="sorttable_sortfwdind" v-if="heading.sortKey === dashboarditem.sort && dashboarditem.direction === 'asc'">&nbsp;</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in dashboarditem.items" v-bind:key="item.Kontraktnr" @click="showKontraktItem(item)">
            <td><a href="javascript:;" class="primary-title">{{item.Kontraktnr}}</a></td>
            <td class="nowrap" v-if="item.kunde.Bedrift">{{item.kunde.FirmaNavn || item.kunde.full_name}}</td>
            <td class="nowrap" v-else>{{item.kunde.full_name}}</td>

            <td class="align-right">{{item.total_objekt | toInteger}}</td>
            <td class="align-right statusicon">
              <span v-if="item.Legitimert" class="statusicon verified"><svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-success"></use></svg></span>
              <span v-else class="statusicon unverified"><svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-error"></use></svg></span>
            </td>
            <td class="align-right statusicon">
              <span v-if="item.Registrertsignert" class="statusicon verified"><svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-success"></use></svg></span>
              <span v-else class="statusicon unverified"><svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-error"></use></svg></span>
            </td>
            <td class="align-right statusicon">
              <span v-if="hasKontonr(item)" class="statusicon verified"><svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-success"></use></svg></span>
              <span v-else class="statusicon unverified"><svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-error"></use></svg></span>
            </td>
            <td class="align-right"><span class="opacity-60">{{item.Sist_endret_tid | avantidate}}</span></td>
            <td class="align-right">
              <span v-if="item.mottaker" class="avatar tiny name left" :class="strToAvatarClass(item.mottaker.initials)">{{item.mottaker.initials}} <span class="name">{{item.mottaker.full_name || item.mottaker.initials}}</span></span>
              <span v-else class="avatar tiny name left" :class="strToAvatarClass('-')">- <span class="name">Ingen ansvarlig</span></span>
            </td>
          </tr>
        </tbody>

        <template v-if="large">
          <tfoot v-if="dashboarditem.pages > 1">
            <tr>
              <td :colspan="dashboarditem.headings.length">
                <div class="pagination-container" style="margin-top: 0; padding: 1rem 0;">
                  <paginate v-model="dashboarditem.page"
                    :page-count="dashboarditem.pages"
                    :page-range="3"
                    :click-handler="setPage"
                    :container-class="'pagination'"
                    :prev-text="paginationArrow"
                    :next-text="paginationArrow"
                    :prev-class="'prev'"
                    :next-class="'next'"
                    >
                  </paginate>
                </div>
              </td>
            </tr>
          </tfoot>
        </template>
      </table>

      <div v-if="dashboarditem.items.length < 1" class="placeholder-container center-container active">
        <div class="center-content">
          <div class="placeholder-icon tiny"><svg width="24px" height="24px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-search"></use></svg></div>
          <h4 class="bold">Fant ingen treff</h4>
          <p class="no-margin">Valgt filter returnerte ingen treff.</p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import KontraktService from '@/services/KontraktService'

export default {
  name: 'TableUferdigeKontrakt',
  props: {
    large: {
      type: Boolean,
      required: false,
      default () {
        return true
      }
    },
    limit: {
      type: Number,
      required: true
    },
    brukerId: {
      required: true
    }
  },
  data () {
    return {
      dashboarditem: {
        page: 1,
        pages: 1,
        total: 0,
        items: [],
        loading: true,
        updating: false,
        sort: 'Sist_endret_tid',
        direction: 'desc',
        headings: [
          { name: 'Kontrakt', sortKey: 'Kontraktnr', class: '', skelTh: 'sk-text sk-text-narrow', skelTd: 'sk-text sk-text-narrow' },
          { name: 'Selger', sortKey: 'kunde.Fnavn', class: 'nowrap', skelTh: 'nowrap sk-text sk-text-narrow', skelTd: 'nowrap sk-text sk-text-narrow' },
          { name: 'Obj.', sortKey: 'total_objekt', class: 'align-right', skelTh: 'align-right sk-text sk-text-narrow', skelTd: 'align-right sk-text sk-text-narrow' },
          { name: 'Leg.', sortKey: 'Legitimert', class: 'align-right statusicon', skelTh: 'align-right statusicon', skelTd: 'align-right statusicon' },
          { name: 'Sign.', sortKey: 'Registrertsignert', class: 'align-right statusicon', skelTh: 'align-right statusicon', skelTd: 'align-right statusicon' },
          { name: 'Kontonr', sortKey: 'Kontonummer', class: 'align-right statusicon', skelTh: 'align-right statusicon', skelTd: 'align-right statusicon' },
          { name: 'Endret', sortKey: 'Sist_endret_tid', class: 'align-right', skelTh: 'align-right sk-text sk-text-narrow', skelTd: 'align-right sk-text sk-text-narrow' },
          { name: 'Ansv.', sortKey: 'mottaker.Fornavn', class: 'align-right', skelTh: 'align-right sk-text sk-text-narrow', skelTd: 'align-right sk-circle sk-circle-tiny' }
        ]
      }
    }
  },
  filters: {
    toInteger (value) {
      return !value ? 0 : parseInt(value)
    }
  },
  created () {
    this.dashboarditem.loading = true

    this.fetch()
  },
  computed: {
    sortClassUnsigned: function () {
      return 'sorttable_sorted' + (this.dashboarditem.direction !== 'asc' ? '_reverse' : '')
    },
    skeletonThLines: function () {
      return this.dashboarditem.headings.map(o => o.skelTh)
    },
    skeletonTdLines: function () {
      const retval = []

      const tdvals = this.dashboarditem.headings.map(o => o.skelTd)

      for (let i = 0; i < this.limit; ++i) {
        retval.push(tdvals)
      }

      return retval
    }
  },
  watch: {
    brukerId: function (newVal) {
      if (newVal !== undefined) {
        this.dashboarditem.page = 1
        this.dashboarditem.updating = true
        this.fetch()
      }
    }
  },
  methods: {
    setPage: function (page) {
      const vm = this

      window.jQuery('html').animate({
        scrollTop: 0
      }, 200, function () {
        vm.dashboarditem.page = page
        vm.dashboarditem.updating = true
        vm.fetch()
      })
    },
    changeSorting: function (sortKey) {
      if (!sortKey) {
        return
      }

      if (this.dashboarditem.sort === sortKey) {
        this.dashboarditem.direction = this.dashboarditem.direction === 'asc' ? 'desc' : 'asc'
      } else {
        this.dashboarditem.direction = 'asc'
      }

      this.dashboarditem.sort = sortKey
      this.dashboarditem.page = 1
      this.dashboarditem.updating = true

      this.fetch()
    },
    fetch: function () {
      let queryParams = {
        page: this.dashboarditem.page,
        sort: this.dashboarditem.sort,
        direction: this.dashboarditem.direction,
        limit: this.limit,
        ansvarlig: this.brukerId
      }

      KontraktService.getNotReady(queryParams)
        .then((response) => {
          this.dashboarditem.total = response.headers['x-paging-count'] ? parseInt(response.headers['x-paging-count']) : 0
          this.dashboarditem.pages = response.headers['x-paging-pagecount'] ? parseInt(response.headers['x-paging-pagecount']) : 1
          this.dashboarditem.items = response.data
        })
        .catch((error) => {
          this.$helpers.showErrorAlert('Feil', 'Kunne ikke laste inn usignerte kontrakter')
          console.log(error)
        })
        .then(() => {
          this.dashboarditem.loading = false
          this.dashboarditem.updating = false

          this.$emit('tableLoaded', this.dashboarditem)
        })
    },
    hasKontonr: function (item) {
      return item.Kontonummer && item.kunde.Kontonr
    },
    showKontraktItem: function (item) {
      this.$router.push({
        name: 'KundeCard',
        params: { kunde_id: item.Kundenr },
        query: {kontrakt: item.Kontraktnr},
        hash: ''
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Admin/Dashboard/TableUferdigeKontrakt.vue